import { Component } from '@angular/core';
import { LOW_VOLUME, MARCH, MIXED, MONTHLY, count, currency, dollars } from './mock-dual-chart-data';

@Component({
  selector: 'app-chart-demo',
  standalone: false,
  template: `
    <div class="page">
      <section>
        <h3>March — 31 days, quartiles in every tooltip</h3>
        <p>Mar 01–08 are real rows. Mar 02 and Mar 07 have zero returns; the rest carry a value.</p>
        <app-dual-axis-chart
          [values]="march"
          [property1]="'total_volume'"
          [property2]="'txn_count'"
          [xLabelProperty]="'local_day'"
          [reversalsProperty]="'total_reversals'"
          [p25Property]="'p25'"
          [medianProperty]="'median'"
          [p75Property]="'p75'"
          [transactionsProperty]="'transactions'"
          label1="Daily Volume"
          label2="Transactions"
          [ratio]="2.6"
          [format1]="currency"
          [format2]="count" />
      </section>

      <section>
        <h3>Low volume — transaction list in the tooltip</h3>
        <p>Sep 03, 05 and 09 are empty days: header only, no footer. Sep 05 has returns but no sales.</p>
        <app-dual-axis-chart
          [values]="lowVolume"
          [property1]="'total_volume'"
          [property2]="'txn_count'"
          [xLabelProperty]="'local_day'"
          [reversalsProperty]="'total_reversals'"
          [p25Property]="'p25'"
          [medianProperty]="'median'"
          [p75Property]="'p75'"
          [transactionsProperty]="'transactions'"
          label1="Daily Volume"
          label2="Transactions"
          [ratio]="2.4"
          [format1]="dollars"
          [format2]="count" />
      </section>

      <section>
        <h3>Mixed — quartile days, list days and empty days side by side</h3>
        <p>Card height jumps between neighbours. This is the layout case most likely to look wrong.</p>
        <app-dual-axis-chart
          [values]="mixed"
          [property1]="'total_volume'"
          [property2]="'txn_count'"
          [xLabelProperty]="'local_day'"
          [reversalsProperty]="'total_reversals'"
          [p25Property]="'p25'"
          [medianProperty]="'median'"
          [p75Property]="'p75'"
          [transactionsProperty]="'transactions'"
          label1="Daily Volume"
          label2="Transactions"
          [ratio]="2.6"
          [format1]="currency"
          [format2]="count" />
      </section>

      <section>
        <h3>No returns row — quartiles only</h3>
        <p>Same data with reversalsProperty omitted. Header drops back to two rows.</p>
        <app-dual-axis-chart
          [values]="mixed"
          [property1]="'total_volume'"
          [property2]="'txn_count'"
          [xLabelProperty]="'local_day'"
          [p25Property]="'p25'"
          [medianProperty]="'median'"
          [p75Property]="'p75'"
          [transactionsProperty]="'transactions'"
          label1="Daily Volume"
          label2="Transactions"
          [ratio]="2.6"
          [format1]="currency"
          [format2]="count" />
      </section>

      <section>
        <h3>Header only — no optional inputs at all</h3>
        <p>Every optional property omitted. Tooltip is the two-row card.</p>
        <app-dual-axis-chart
          [values]="mixed"
          [property1]="'total_volume'"
          [property2]="'txn_count'"
          [xLabelProperty]="'local_day'"
          label1="Daily Volume"
          label2="Transactions"
          [ratio]="2.6"
          [format1]="currency"
          [format2]="count" />
      </section>

      <section>
        <h3>Custom headings and label</h3>
        <p>statsHeading, listHeading and reversalsLabel are all inputs.</p>
        <app-dual-axis-chart
          [values]="march"
          [property1]="'total_volume'"
          [property2]="'txn_count'"
          [xLabelProperty]="'local_day'"
          [reversalsProperty]="'total_reversals'"
          [p25Property]="'p25'"
          [medianProperty]="'median'"
          [p75Property]="'p75'"
          [transactionsProperty]="'transactions'"
          reversalsLabel="Refunds"
          statsHeading="TICKET SIZE"
          listHeading="THE DAY'S ACTIVITY"
          label1="Daily Volume"
          label2="Transactions"
          [ratio]="2.6"
          [format1]="currency"
          [format2]="count" />
      </section>

      <section>
        <h3>13 months — even spacing, wide bars</h3>
        <app-dual-axis-chart
          [values]="monthly"
          [property1]="'monthly_volume'"
          [property2]="'txn_count'"
          [xLabelProperty]="'label'"
          [reversalsProperty]="'monthly_reversals'"
          label1="Monthly Volume"
          label2="Transactions"
          [ratio]="3.2"
          [format1]="currency"
          [format2]="count" />
      </section>

      <section>
        <h3>13 months — positioned by epoch (xValuesProperty)</h3>
        <app-dual-axis-chart
          [values]="monthly"
          [property1]="'monthly_volume'"
          [property2]="'txn_count'"
          [xLabelProperty]="'label'"
          [xValuesProperty]="'epoch'"
          [reversalsProperty]="'monthly_reversals'"
          label1="Monthly Volume"
          label2="Transactions"
          [ratio]="3.2"
          [format1]="currency"
          [format2]="count" />
      </section>

      <section>
        <h3>Custom colours</h3>
        <p>Pass tokens with a fallback so an unreachable variable still renders.</p>
        <app-dual-axis-chart
          [values]="march"
          [property1]="'total_volume'"
          [property2]="'txn_count'"
          [xLabelProperty]="'local_day'"
          [reversalsProperty]="'total_reversals'"
          [p25Property]="'p25'"
          [medianProperty]="'median'"
          [p75Property]="'p75'"
          color1="var(--green-800, #1f7a4d)"
          color2="var(--archive-red, #c0392b)"
          label1="Daily Volume"
          label2="Transactions"
          [ratio]="2.6"
          [format1]="currency"
          [format2]="count" />
      </section>

      <section>
        <h3>Narrow container — text scales down with width</h3>
        <div style="width: 420px">
          <app-dual-axis-chart
            [values]="lowVolume"
            [property1]="'total_volume'"
            [property2]="'txn_count'"
            [xLabelProperty]="'local_day'"
            [reversalsProperty]="'total_reversals'"
            [transactionsProperty]="'transactions'"
            label1="Daily Volume"
            label2="Transactions"
            [ratio]="1.8"
            [format1]="dollars"
            [format2]="count" />
        </div>
      </section>
    </div>
  `,
  styles: [`
    .page { max-width: 1200px; margin: 0 auto; padding: 24px; font-family: system-ui, sans-serif; }
    section { margin-bottom: 44px; min-width: 480px; }
    h3 { font-size: 13px; font-weight: 600; color: #5b6874; margin: 0 0 2px; }
    p { font-size: 12px; color: #93a1ad; margin: 0 0 10px; }
  `],
})
export class DualChartDemoComponent {
  march = MARCH;
  lowVolume = LOW_VOLUME;
  mixed = MIXED;
  monthly = MONTHLY;

  currency = currency;
  dollars = dollars;
  count = count;
}